import React, { useEffect, useRef } from "react";

import { Box, keyframes, Stack, TextField, Typography } from "@mui/material";

import Label from "../../../../common/components/Label";
import { COLOR_MAP } from "../../../../constants/ColorNames";
import TextBox from "../../../../common/components/Textbox";
import { useDispatch, useSelector } from "react-redux";
import { setCompanyName, setAboutCompany, setIsMediaUploadAllowed, setCompanyReplyId, setIsMaxBlastLimitReached, setShowPassiveJobs, setIsBridgeCustomerState, setOriginalCompanyName, } from "../../../../redux/slice/campaignSlice";
import { RootState } from "../../../../redux/store";
import { useGetCompanyDetailsQuery, useGetInventoryPurchasedQuery } from "../../../../redux/services/folderName";
import styled from "@emotion/styled";
import CompanyMedia from "./CompanyMedia";
import InfoBridge from "./InfoBridge";
import { BoxMessage } from "../../../../common/components/CustomMuiComponents";
import MsgBulbIcon from "../../../../constants/icons/MsgBulbIcon";
import {eventPushFunction} from "@foundit/clevertap"

const CompanyDetailsInput = styled(TextField)`
.MuiInputBase-input {
  overflow-y: auto;
  &::-webkit-scrollbar {
    display: none;
  }
}
`

type CompanyEditableType = {
  canEditAbout : boolean, 
  canEditName : boolean,
}
const initialCompanyEditableFlag : CompanyEditableType  = {
  canEditAbout : true, 
  canEditName : true,
}

const shimmerKeyframe = keyframes`
  0% {
    background-position-x: 100%;
  }
  33% {
    background-position-x: 0%;
  }
  100% {
    background-position-x: 0%;
  }
`;

const CompanyProfile = () => {
    // const [companyName, setCompanyName] = useState<string>('Wipro technologies')
    // const [companyDetails, setCompanyDetails] = useState<string>('Wipro technologies is an Integrated HR Service Provider for all Corporates all over India. We are backed by a good ERP and enough experience in HR and relate')

    const dispatch = useDispatch();
    const companyName = useSelector((state: RootState) => state.campaign.companyName);
    const aboutCompany = useSelector((state: RootState) => state.campaign.aboutCompany);
    const [isCompanyDataEditable, setIsCompanyDataEditable] = React.useState<CompanyEditableType>(initialCompanyEditableFlag)
    const [isBridgeCustomer, setIsBridgeCustomer] = React.useState<boolean>(false);
    const isMediaAllowed = useSelector((state: RootState) => state.campaign.isMediaUploadAllowed);
    const media = useSelector((state: RootState) => state.campaign.mediaList);
    const userDetails = useSelector((state: any) => state.campaign.userDetails);
    const isMediaFetched = useSelector((state: RootState) => state.campaign.isMediaFetched);
    
    const isFirstRenderRef = useRef<boolean>(true);

    const {data: companyDetails} = useGetCompanyDetailsQuery(1);
    const userData = useSelector((state: RootState) => state.campaign.userData);
    const { data: inventoryPurchasedData } = useGetInventoryPurchasedQuery(userData?.corpId, {skip: !userData?.corpId})

    useEffect(()=>{
      if(inventoryPurchasedData){
        const isBridgeType = inventoryPurchasedData?.account_type?.toLowerCase() !== "purchase"
        setIsBridgeCustomer(isBridgeType);
        dispatch(setIsBridgeCustomerState({isBridgeCustomer : isBridgeType}))
      }
    },[dispatch, inventoryPurchasedData])

    React.useEffect(()=> {
      if(companyDetails){
        dispatch(setCompanyName({ companyName: companyDetails.name}));
        dispatch(setOriginalCompanyName({ companyName: companyDetails.name}));
        dispatch(setAboutCompany({ aboutCompany: companyDetails.profile}));
        dispatch(setCompanyReplyId({companyReplyId: companyDetails.email}));
        if(companyDetails.isBlastAllowed === false){
          dispatch(setIsMaxBlastLimitReached({isMaxBlastLimitReached: true}))
        }
        dispatch(setShowPassiveJobs({showPassiveJobs: companyDetails.isPassiveJobsEnabled}))
        dispatch(setIsMediaUploadAllowed({
          isMediaUploadAllowed : {
          cover : companyDetails.canUploadBanner,
          logo: companyDetails.canUploadLogo
        }}))
        setIsCompanyDataEditable({
          canEditAbout : companyDetails.canEditAbout,
          canEditName: companyDetails.canEditName,
        })
      }
    },[companyDetails, dispatch])

    const handleCompanyDetailsChange = React.useCallback((newValue: string, type: string) => {
       if(type === 'companyName'){
        dispatch(setCompanyName({ companyName: newValue}));
       }
       else{
        dispatch(setAboutCompany({ aboutCompany: newValue}));
       }
    }, [dispatch])


    useEffect(() => {
      if (
        isMediaFetched &&
        userDetails?.email &&
        companyDetails &&
        isFirstRenderRef.current
      ) {
        // clevertap event for employer branding view
        eventPushFunction({
          eventName: "Employer_Branding_Viewed",
          data: {
            logo_uploaded: !!media?.logo ? "Yes" : "No",
            company_name: companyDetails?.name || "",
            banner_uploaded: !!media?.cover ? "Yes" : "No",
            description: companyDetails?.profile || "",
            xcode: userDetails?.x_code || "",
            subuid: userDetails?.recruiter_id || "",
          },
        });
        isFirstRenderRef.current = false;
      }
    }, [
      companyDetails,
      userDetails?.email,
      userDetails?.recruiter_id,
      userDetails?.x_code,
      isMediaFetched,
      media?.logo,
      media?.cover,
    ]);

    return (
      <Stack
        padding="1.6rem"
        rowGap="16px"
        borderBottom={`0.1rem solid ${COLOR_MAP.BORDER_GREY}`}
      >
        <Stack>
          <Label colorType="dark" label="Company profile" />
        </Stack>
        {(isMediaAllowed?.logo && isMediaAllowed?.cover) && <>
          <Stack>
            <BoxMessage
              sx={{
                backgroundColor: COLOR_MAP.NOTIFICATION_BOX,
                position: "relative",
                padding: "16px",
                display: "flex",
                alignItems: "center",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  background: `linear-gradient(-60deg, transparent 45%, rgba(255, 255, 255, 0.8) 46%, transparent 47%)`,
                  backgroundSize: "300%",
                  backgroundPositionX: "100%",
                  animation: `${shimmerKeyframe} 4.5s infinite linear`,
                  zIndex: 1,
                  pointerEvents: "none",
                }}
              />
              <Box sx={{ height: "16px", width: "16px" }}>
                <MsgBulbIcon />
              </Box>
              <Typography sx={{ fontSize: "inherit" }}>
                Unlock up to <span style={{ fontWeight: 600 }}>3X higher</span>{" "}
                responses with company branding and details!
              </Typography>
            </BoxMessage>
          </Stack>
          <Stack>
            <CompanyMedia />
          </Stack>
          <Stack>
            <div style={{ borderTop: "1px dashed #E8E8E8", width: "100%" }} />
          </Stack>
        </>
        }    
        <Stack rowGap="0.3rem">
          <Label
            colorType="light"
            label="Company Name"
            labelFor="company-name"
          />
          <TextBox
            id="company-name"
            placeholder="Name of the company"
            textValue={companyName}
            disabled={!isCompanyDataEditable.canEditName}
            handleChange={(e) =>
              handleCompanyDetailsChange(e.target.value, "companyName")
            }
          />
        </Stack>
        <Stack rowGap="0.3rem">
          <Label
            colorType="light"
            label="About company"
            labelFor="about-company"
          />
          <CompanyDetailsInput
            id="outlined-multiline-static"
            multiline
            rows={4}
            value={aboutCompany}
            disabled={!isCompanyDataEditable.canEditAbout}
            onChange={(e: React.BaseSyntheticEvent) =>
              handleCompanyDetailsChange(e.target.value, "companyDetails")
            }
          />
        </Stack>
        {isBridgeCustomer ? <InfoBridge /> : null}
      </Stack>
    );
}

export default CompanyProfile;